import max from 'lodash/max';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import type { ExtendedSubjectProps } from '../../../StudyPage/Campus/Sections/StudyModelSection/Components/StudyModelGrid/StudyModelGrid';
import type { IStudyModelSectionBlockViewModel } from '../ViewModel/IStudyModelSectionBlockViewModel.csharp';
import type { LegacyStudyModelSectionBlockViewModel } from '../ViewModel/Legacy/LegacyStudyModelSectionBlockViewModel.csharp';
import type { StudyModelSectionBlockSubjectOption } from '../ViewModel/StudyModelSectionBlockSubjectOption.csharp';
import type { StudyModelSectionBlockSubjectViewModel } from '../ViewModel/StudyModelSectionBlockSubjectViewModel.csharp';
import type { StudyModelSectionBlockViewModel } from '../ViewModel/StudyModelSectionBlockViewModel.csharp';
import { StudyModelSectionBlockViewModelType } from '../ViewModel/StudyModelSectionBlockViewModelType.csharp';
import type { StudyModelSectionTranslations } from '../ViewModel/StudyModelSectionTranslations.csharp';
import type { StudyModelSettingsViewModel } from '../ViewModel/StudyModelSettingsViewModel.csharp';
import type { AvailableYearProps } from '~/Features/StudyPage/Campus/Sections/StudyModelSection/Components/StudyModelGrid/StudyModelGrid';
import type { StudyModelSectionAccordionItemSettings } from '~/Features/StudyPage/Campus/Sections/StudyModelSection/StudyModelSection';
import { asHtmlString } from '~/Partials/Controls/Utils/asHtmlString';
import { createArray, capitalize, isTruthy } from '~/Partials/Utils/utils';

export const mapSubjects = (subjects: StudyModelSectionBlockSubjectViewModel[]): ExtendedSubjectProps[] =>
    subjects.map(subject => ({
        ...subject,
        description: asHtmlString(subject.description),
    }));

export const mapSettings = (settings: StudyModelSettingsViewModel): StudyModelSectionAccordionItemSettings => {
    const { disableStudyModel, disabledStudyModelHeading, disabledStudyModelMessage } = settings;

    return {
        isDisabled: disableStudyModel,
        disabledMessageHeading: disabledStudyModelHeading,
        disabledMessage: disabledStudyModelMessage,
    };
};

export const getAvailableYears = (
    subjects: StudyModelSectionBlockSubjectViewModel[],
    translations: StudyModelSectionTranslations
): AvailableYearProps[] => {
    const buildSemesterHeading = (semesterNumber: number) => `${semesterNumber}. ${translations.semester}`;
    const buildYearLabel = (yearNumber: string) => `${capitalize(yearNumber)} ${translations.year}`;

    const yearTranslationsMapper = [
        translations.first,
        translations.second,
        translations.third,
        translations.fourth,
        translations.fifth,
    ];

    const getMaxYear = () => {
        const maxYear = max(subjects.map(subject => subject.year)) ?? 0;

        if (maxYear > yearTranslationsMapper.length) {
            console.error('Could not render more years due to lack of translations');

            return yearTranslationsMapper.length;
        }

        return maxYear;
    };

    const maxYear = getMaxYear();
    const hasPrerequisites = subjects.find(subject => subject.isPrerequisite) !== undefined;

    const availableYearProps: AvailableYearProps[] = [];

    if (hasPrerequisites) {
        availableYearProps.push({
            uniqueId: 0,
            label: translations.prerequisites,
            firstHeading: translations.prerequisites,
            secondHeading: '',
        });
    }

    if (maxYear === 1) {
        availableYearProps.push({
            uniqueId: 1,
            label: buildYearLabel(translations.single),
            firstHeading: buildSemesterHeading(1),
            secondHeading: buildSemesterHeading(2),
            isDefault: true,
        });
    } else {
        availableYearProps.push(
            ...createArray(
                index => ({
                    uniqueId: index + 1,
                    label: buildYearLabel(yearTranslationsMapper[index]),
                    firstHeading: buildSemesterHeading(2 * index + 1),
                    secondHeading: buildSemesterHeading(2 * index + 2),
                    isDefault: index === 0,
                }),
                maxYear
            )
        );
    }

    return availableYearProps;
};

export const mapToOptions = (allPossibleValues: (string | undefined)[]) => {
    const uniqueValues = uniq(allPossibleValues).filter(isTruthy);

    return new Map(uniqueValues.map(value => [value, value]));
};

export const mapSubjectsOptions = (allValues: (StudyModelSectionBlockSubjectOption | undefined)[]) => {
    const definedOptions = allValues.filter(isTruthy);
    const orderedValues = orderBy(definedOptions, option => option.isDefault, 'desc').map(value => value.label);

    return mapToOptions(orderedValues);
};

export const isLegacyView = (
    viewModel: IStudyModelSectionBlockViewModel
): viewModel is LegacyStudyModelSectionBlockViewModel =>
    viewModel.type === StudyModelSectionBlockViewModelType.LegacyView;

export const isNewView = (viewModel: IStudyModelSectionBlockViewModel): viewModel is StudyModelSectionBlockViewModel =>
    viewModel.type === StudyModelSectionBlockViewModelType.View;
